import { useEffect, useRef } from 'react'
import type { StorefrontBlogPost } from '../lib/dropify'
import {
  trackNewsArticleProgress,
  trackNewsArticleView,
  trackNewsListView,
  trackWhatsAppLead,
  whatsAppCtaProps,
} from '../lib/analytics'

type NewsListTrackerProps = {
  listName: string
  posts: StorefrontBlogPost[]
}

export function NewsListTracker({ listName, posts }: NewsListTrackerProps) {
  const trackedKey = useRef<string | null>(null)

  useEffect(() => {
    const key = `${listName}:${posts.map((post) => post.id).join(',')}`
    if (posts.length === 0 || trackedKey.current === key) return
    trackedKey.current = key
    trackNewsListView(listName, posts)
  }, [listName, posts])

  return null
}

type NewsArticleTrackerProps = {
  post: StorefrontBlogPost
  articleElementId: string
}

const READ_MILESTONES = [25, 50, 75, 90] as const

// Article bodies come from the dashboard CMS as raw HTML, so WhatsApp CTAs
// inside them cannot attach onClick handlers. Delegate clicks instead; authors
// tag anchors with data-lp-category / data-lead-item to refine the lead params.
export function trackNewsContentWhatsAppClick(
  event: { target: EventTarget | null },
  post: Pick<StorefrontBlogPost, 'title'>,
): void {
  if (!(event.target instanceof Element)) return
  const anchor = event.target.closest('a[href*="wa.me/"]')
  if (!(anchor instanceof HTMLAnchorElement)) return

  trackWhatsAppLead('news', {
    cta_placement: anchor.dataset.ctaPlacement || 'news_article_closing',
    lp_category: anchor.dataset.lpCategory || 'Berita',
    lead_item_name: anchor.dataset.leadItem || post.title,
  })
}

export function NewsArticleTracker({ post, articleElementId }: NewsArticleTrackerProps) {
  useEffect(() => {
    trackNewsArticleView(post)

    const article = document.getElementById(articleElementId)
    if (!article) return

    article.querySelectorAll<HTMLAnchorElement>('a[href*="wa.me/"]').forEach((anchor) => {
      anchor.setAttribute('data-wa-cta', whatsAppCtaProps('news', {
        cta_placement: anchor.dataset.ctaPlacement || 'news_article_closing',
        lp_category: anchor.dataset.lpCategory || 'Berita',
        lead_item_name: anchor.dataset.leadItem || post.title,
      })['data-wa-cta'])
    })

    const reached = new Set<number>()
    let frame = 0

    const handleClick = (event: MouseEvent) => trackNewsContentWhatsAppClick(event, post)
    article.addEventListener('click', handleClick)

    const measure = () => {
      frame = 0
      const rect = article.getBoundingClientRect()
      const articleTop = window.scrollY + rect.top
      const visibleDepth = window.scrollY + window.innerHeight - articleTop
      const progress = Math.max(0, Math.min(100, (visibleDepth / Math.max(rect.height, 1)) * 100))

      for (const milestone of READ_MILESTONES) {
        if (progress >= milestone && !reached.has(milestone)) {
          reached.add(milestone)
          trackNewsArticleProgress(post, milestone)
        }
      }
    }

    const scheduleMeasure = () => {
      if (!frame) frame = window.requestAnimationFrame(measure)
    }

    scheduleMeasure()
    window.addEventListener('scroll', scheduleMeasure, { passive: true })
    window.addEventListener('resize', scheduleMeasure)

    return () => {
      article.removeEventListener('click', handleClick)
      window.removeEventListener('scroll', scheduleMeasure)
      window.removeEventListener('resize', scheduleMeasure)
      if (frame) window.cancelAnimationFrame(frame)
    }
  }, [articleElementId, post])

  return null
}
